import { Box, Container, Typography, useMediaQuery } from '@mui/material'
import { GridActionsCellItem, GridColumns, GridRenderCellParams, GridValueGetterParams } from '@mui/x-data-grid'
import { Avatar, Icon, IconToggle, LinearProgressBar, SnackBarContext, SwedSecButton } from 'components'
import { inject, observer } from 'mobx-react'
import { Curriculum, Package, User, Company } from 'models'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { RoutingParams } from 'routes'
import { AuthStore, RoutingStore } from 'stores'
import { DataType, DataTypeContent, ICurriculum, UserType } from 'types'
import { caseInsensitiveComparator } from 'util/util'
import { theme } from 'config'
import { Loading } from 'views/loading'
import ParticipantDataGrid from './participantDataGrid'
import ParticipantActionButtons from './participantActionButtons'

export interface IContentUserListProps {
  authStore?: AuthStore
  routingStore?: RoutingStore
  contentType: DataTypeContent
}

type RowData = {
  id: number
  user: User
  name: string
  progress: number
  email: string
  num_programs?: number
}

export const ParticipantView = inject(
  'authStore',
  'routingStore'
)(
  observer((props: IContentUserListProps): JSX.Element => {
    const { t } = useTranslation()
    const { contentType, authStore } = props
    const { toast } = useContext(SnackBarContext)
    const params: RoutingParams = useParams<RoutingParams>()
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

    const [content, setContent] = useState<Curriculum<ICurriculum> | null>(null)
    const [participants, setParticipants] = useState<User[]>([])
    const [showFakeData, setShowFakeData] = useState<boolean>(false)
    const [selectedRowsIds, setSelectedRowsIds] = useState<number[]>([])
    const [allUsers, setAllUsers] = useState<User[]>([])
    const [isAssigning, setIsAssigning] = useState<boolean>(false)
    const [isUnassigning, setIsUnassigning] = useState<boolean>(false)
    const [loading, setLoading] = useState(true)
    const [loadingUsers, setLoadingUsers] = useState(true)

    const statistics = authStore?.getCurUser?.statistics

    const company_id: number | undefined =
      authStore?.getCurUserType === UserType.Admin
        ? Number(params.id)
        : undefined

    const content_id: number =
      authStore?.getCurUserType === UserType.Admin
        ? Number(params.subid)
        : Number(params.id)
    
        // Fetch content and participants
        useEffect(() => {
          let isMounted = true

          const fetchData = async () => {
            if (!content_id) {
              if (isMounted) {
                setContent(null)
                setParticipants([])
                setLoading(false)
              }
              return
            }

            try {
              if (isMounted) {
                setLoading(true)
              }

              // Fetch content first
              const result = await Curriculum.getContent(
                contentType,
                content_id,
                true,
                company_id
              )

              // Handle the case where result could be an array or a single item
              const curriculumItem = Array.isArray(result) ? result[0] : result

              if (!curriculumItem) {
                console.warn(
                  'No curriculum item found',
                  contentType,
                  content_id,
                  company_id
                )
                if (isMounted) {
                  toast('No content available', 'warning')
                  setLoading(false)
                }
                return
              }

              // Check if component is still mounted before updating state
              if (!isMounted) return

              setContent(curriculumItem)

              // Fetch participants using the curriculum item
              const users = await User.getParticipants(
                contentType,
                curriculumItem,
                company_id
              )

              // Check mount status again after participant fetch
              if (!isMounted) return

              // Validate and set participants
              const validUsers = users?.filter((user) => user && user.id) || []
              setParticipants(validUsers)

              if (validUsers.length) {
                // Single, optimized statistics request
                await authStore.getCurUser.loadStatistics({
                  user_id: validUsers.map((p) => p.id),
                  company_id,
                })
              }
            } catch (error) {
              if (isMounted) {
                console.error('Error loading content and participants:', error)
                toast('Failed to load data', 'error')
              }
            } finally {
              if (isMounted) {
                setLoading(false)
              }
            }
          }

          fetchData()

          return () => {
            isMounted = false
          }
        }, [content_id, contentType, company_id, toast, authStore])

    useEffect(() => {
      const fetchAllUsers = async () => {
        try {
          setLoadingUsers(true)
          let fetchedUsers: User[] = []

          if (company_id) {
            const result = await Company.get(company_id)
            if (!result || Array.isArray(result)) {
              throw new Error('Failed to load company')
            }

            const company = result
            const success = await company.loadUsers()
            if (!success) throw new Error('Failed to load company users')
            fetchedUsers = company.users
          } else {
            const result = await User.get()
            if (!result || !Array.isArray(result)) {
              throw new Error('Failed to load users')
            }
            fetchedUsers = result
          }
          setAllUsers(fetchedUsers)
        } catch (err) {
          console.error(err)
          toast('Failed to fetch users', 'error')
        } finally {
          setLoadingUsers(false)
        }
      }

      if (contentType === DataType.Program) {
        fetchAllUsers()
      } else {
        setLoadingUsers(false)
      }
    }, [company_id, contentType, toast])

    const rows: RowData[] = useMemo(() => {
      if (!participants?.length) return []

      return participants
        .map((participant) => {
          const contentStat = statistics?.user?.[participant.id]?.[contentType]
          const contentId = content?.id

          return {
            id: participant.id,
            user: participant,
            name: participant.getFullName,
            progress:
              contentId && contentStat
                ? Math.round(contentStat[contentId]?.progress || 0)
                : 0,
            email: participant.email,
            num_programs:
              contentId && contentStat
                ? contentStat[contentId]?.num_programs
                : 0,
            hasLicense: true,
          }
        })
        .sort((a, b) => a.name.localeCompare(b.name))
    }, [participants, statistics, content, contentType])

    const unassignedUsers = useMemo(
      () =>
        allUsers
          .filter((user) => !participants.some((p) => p.id === user.id))
          .map((user) => ({
            id: user.id,
            user: user,
            name: user.getFullName,
            progress: 0,
            email: user.email,
            num_programs: 0,
            hasLicense: false,
          }))
          .sort((a, b) => a.name.localeCompare(b.name)),
      [allUsers, participants]
    )

    const selectedEmails = useMemo(
      () =>
        selectedRowsIds
          .map((id) => {
            const user =
              rows.find((row) => row.id === id) ||
              unassignedUsers.find((row) => row.id === id)
            return user?.email
          })
          .filter(Boolean),
      [selectedRowsIds, rows, unassignedUsers]
    )

    const handleAssignUsers = useCallback(async () => {
      if (selectedRowsIds.length === 0 || !content) return

      setIsAssigning(true)

      try {
        const usersToAssign = allUsers.filter(
          (user) =>
            selectedRowsIds.includes(user.id) &&
            !participants.some((p) => p.id === user.id)
        )

        let success = true
        const newParticipants = [...participants]

        for (const user of usersToAssign) {
          const result = await user.linkContent(
            contentType,
            content.id,
            company_id
          )

          if (!result) {
            success = false
            toast(`Failed to assign ${user.getFullName}`, 'error')
          } else {
            newParticipants.push(user)
          }
        }

        if (success) {
          toast(t('toast_user_assigned_successfully'), 'success')
        }

        setParticipants(newParticipants)
        setSelectedRowsIds([])
      } catch (error) {
        console.error('Error assigning users:', error)
        toast('An error occurred while assigning users', 'error')
      } finally {
        setIsAssigning(false)
      }
    }, [
      selectedRowsIds,
      allUsers,
      participants,
      content,
      contentType,
      company_id,
      toast,
    ])

    const handleUnassignUsers = useCallback(async () => {
      if (selectedRowsIds.length === 0 || !content) return

      setIsUnassigning(true)

      try {
        const usersToUnassign = participants.filter((user) =>
          selectedRowsIds.includes(user.id)
        )

        let success = true

        for (const user of usersToUnassign) {
          const result = await user.unlinkContent(
            contentType,
            content.id,
            company_id
          )

          if (!result) {
            success = false
            toast(`Failed to unassign ${user.getFullName}`, 'error')
          }
        }

        if (success) {
          toast(t('toast_user_unassigned_successfully'), 'success')
          setParticipants(
            participants.filter((p) => !selectedRowsIds.includes(p.id))
          )
        }

        setSelectedRowsIds([])
      } catch (error) {
        console.error('Error unassigning users:', error)
        toast('An error occurred while unassigning users', 'error')
      } finally {
        setIsUnassigning(false)
      }
    }, [selectedRowsIds, participants, content, contentType, company_id, toast])

    const toggleFakeData = useCallback(() => {
      setShowFakeData((prev) => !prev)
    }, [])

    const handleSendEmail = useCallback(() => {
      if (selectedEmails.length > 0) {
        window.location.href = `mailto:${selectedEmails.join(',')}`
      }
    }, [selectedEmails])

    // Column definitions
    const columns: GridColumns = useMemo(() => {
      const baseColumns: GridColumns = [
        {
          field: 'name',
          headerName: t('name'),
          flex: 1,
          headerClassName: 'columnHeader',
          minWidth: 180,
          renderCell: (params: GridRenderCellParams<string, RowData>) => (
            <Avatar
              name={showFakeData ? t('fake_user_name') : undefined}
              fakeEmail={showFakeData}
              user={params.row.user}
            />
          ),
          sortable: true,
          filterable: false,
          valueGetter: (params: GridValueGetterParams) =>
            params.row?.name?.toString().toLowerCase() || '',
          sortComparator: caseInsensitiveComparator,
        },
        {
          flex: 1,
          field: 'progress',
          headerName: t('progress'),
          headerClassName: 'columnHeader',
          minWidth: 150,
          filterable: false,
          sortable: true,
          renderCell: (params: GridRenderCellParams<number, RowData>) => (
            <LinearProgressBar
              label={`${Math.round(params.value) || 0}%`}
              value={Math.round(params.value) || 0}
            />
          ),
        },
        {
          field: 'hasLicense',
          headerName: t('license_status'),
          flex: 1,
          minWidth: 120,
          sortable: true,
          headerClassName: 'columnHeader',
          renderCell: (params: GridRenderCellParams<boolean>) => (
            <Typography color={params.value ? 'success.main' : 'error.main'}>
              {params.value ? t('assigned_singular') : t('unassigned_singular')}
            </Typography>
          ),
          valueGetter: (params: GridValueGetterParams) => params.row.hasLicense,
          filterable: true,
          filterOperators: [
            {
              value: 'true',
              label: t('assigned_singular'),
              getApplyFilterFn: () => (params) =>
                params.row.hasLicense === true,
            },
            {
              value: 'false',
              label: t('unassigned_singular'),
              getApplyFilterFn: () => (params) =>
                params.row.hasLicense === false,
            },
          ],
        },
        {
          field: 'action',
          type: 'actions',
          headerAlign: 'center',
          width: 80,
          filterable: false,
          sortable: false,
          renderHeader: () => (
            <IconToggle
              iconOn={<Icon size='size-12' iconId='lock' />}
              iconOff={<Icon size='size-12' iconId='unlock' />}
              selected={showFakeData}
              onChange={toggleFakeData}
              tooltipIconOff={t('hide_real_data')}
              tooltipIconOn={t('show_real_data')}
            />
          ),
          getActions: () => [
            <GridActionsCellItem
              key='show_details'
              icon={<Icon iconId='info' size='size-16' />}
              disabled
              label={t('show_details')}
            />,
          ],
        },
      ]

      if (contentType === DataType.Package) {
        baseColumns.splice(1, 0, {
          flex: 1,
          field: 'num_programs',
          headerName: t('num_programs'),
          headerClassName: 'columnHeader',
          minWidth: 120,
        })
      }

      return baseColumns
    }, [t, showFakeData, contentType, toggleFakeData])

    const selectedIds = useMemo(
      () =>
        selectedRowsIds
          .map((id) => {
            const row = rows.find((r) => r.id === id)
            return row?.user?.personal_number
          })
          .filter(Boolean), // Filter out any undefined values
      [selectedRowsIds, rows]
    )

    const licenseStatusOfSelection = useMemo(() => {
      const selectedAssigned = rows.filter((r) =>
        selectedRowsIds.includes(r.id)
      )
      const selectedUnassigned = unassignedUsers.filter((r) =>
        selectedRowsIds.includes(r.id)
      )

      const hasAssigned = selectedAssigned.length > 0
      const hasUnassigned = selectedUnassigned.length > 0

      if (hasAssigned && hasUnassigned) return 'mixed'
      if (hasAssigned) return 'assigned'
      if (hasUnassigned) return 'unassigned'
      return null
    }, [selectedRowsIds, rows, unassignedUsers])

    if (!content) {
      return (
        <Container maxWidth='xl' className='participant-list-container'>
          <Loading />
        </Container>
      )
    }

    return (
      <Container maxWidth='xl' className='participant-list-container'>
        {/* Header */}
        <Box
          display='flex'
          justifyContent={isMobile ? 'center' : 'space-between'}
          alignItems='center'
          mb={3}
        >
          <Typography variant='h4' fontWeight='bold' color='text.primary'>
            {content?.title}
          </Typography>
        </Box>

        {/* Package description */}
        {contentType === DataType.Package && (
          <Box mb={3}>
            <Typography variant='body1' color='text.secondary'>
              {t('package_userlist_description')}
            </Typography>
          </Box>
        )}

        {/* SwedSecButton for Package */}
        {contentType === DataType.Package && (
          <Box display='flex' justifyContent='flex-start' gap={2} mb={3}>
            <SwedSecButton akuPackage={content as Package} ids={selectedIds} />
          </Box>
        )}

        {/* Action Buttons */}
        <ParticipantActionButtons
          isMobile={isMobile}
          selectedEmails={selectedEmails}
          selectedRowsIds={selectedRowsIds}
          isAssigning={isAssigning}
          isUnassigning={isUnassigning}
          contentType={contentType}
          licenseStatusOfSelection={licenseStatusOfSelection}
          handleSendEmail={handleSendEmail}
          handleUnassignUsers={handleUnassignUsers}
          handleAssignUsers={handleAssignUsers}
        />

        {/* Combined Grid */}
        <ParticipantDataGrid
          rows={[
            ...rows.map((u) => ({ ...u, hasLicense: true })),
            ...unassignedUsers.map((u) => ({ ...u, hasLicense: false })),
          ]}
          columns={columns}
          selectedRowsIds={selectedRowsIds}
          setSelectedRowsIds={setSelectedRowsIds}
          placeholderText={t('datagrid_placeholder_company_user_list')}
          loading={loading}
        />
      </Container>
    )
  })
)
